import type { Axios } from "axios";
import type { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import type { LocalAudioChunk, LocalRecordingSession } from "@/models";
import { localRecordingStorage } from "@/localDb/localRecordingStorage";
import {
  finishRecordingSession,
  startRecordingSession,
  uploadRecordingChunk
} from "@/api/recordings";

/** Prevents concurrent syncs within the *same* tab. */
let isSyncing = false;

export async function syncRecordings(
  secureAxios: Axios,
  appInsights: ReactPlugin
) {
  if (isSyncing) {
    return;
  }

  // Use Web Locks API to prevent concurrent syncs across *multiple* tabs
  await navigator.locks.request(
    "recordings-sync",
    { ifAvailable: true },
    async (lock) => {
      if (!lock) {
        return; // another tab already holds the lock
      }

      isSyncing = true;

      try {
        if (!navigator.onLine) {
          console.log("Offline, skipping sync");
          return;
        }
        const unsynchedRecordingSessions =
          await localRecordingStorage.getUnsynchedRecordingSessions();
        for (const session of unsynchedRecordingSessions) {
          await processUnsynchronizedSession(secureAxios, session, appInsights);
        }
      } finally {
        isSyncing = false;
      }
    }
  );
}

async function processUnsynchronizedSession(
  secureAxios: Axios,
  session: LocalRecordingSession,
  appInsights: ReactPlugin
) {
  if (!session.isSynchonizationStarted) {
    try {
      // tell the server about a new recording session
      await startRecordingSession(
        secureAxios,
        session.id,
        session.startedDate,
        session.prospectId,
        session.recordingTitle,
        session.recordingTypeId
      );
      session.isSynchonizationStarted = true;
      await localRecordingStorage.updateRecordingSession(session);
    } catch (err) {
      console.error(err);
      appInsights.trackException({ error: err as Error });
      return false; // don't try to upload chunks if we couldn't start the recording
    }
  }
  // get all the chunks for this session that are not yet synchronized
  const unsynchedChunks =
    await localRecordingStorage.getSortedChunksForRecordingSession(
      session.id,
      true
    );
  for (const chunk of unsynchedChunks) {
    const successProcessingChunk = await processUnsynchronizedChunk(
      secureAxios,
      chunk,
      session,
      appInsights
    );
    if (!successProcessingChunk) {
      console.error(
        "Stopping sync for this session due to chunk upload failure",
        session.id,
        chunk.chunkId
      );
      break; // stop processing chunks for this session on first failure
    }
  }
}

async function processUnsynchronizedChunk(
  secureAxios: Axios,
  chunk: LocalAudioChunk,
  session: LocalRecordingSession,
  appInsights: ReactPlugin
) {
  if (chunk.binaryData.size === 0) {
    return true; // TODO: doesn't this mean it gets kept forever and neither the session nor the chunk are ever marked as synchronized??
  }
  try {
    // send the chunk to the server with its sequence number for proper ordering
    await uploadRecordingChunk(
      secureAxios,
      session.id,
      session.startedDate,
      chunk.durationAtEndMs,
      chunk.sequenceNumber,
      chunk.binaryData
    );
    // if we awaited and didn't throw, we good
    // mark the chunk as synchronized and update that in indexedDB
    chunk.isSynchronized = true;
    await localRecordingStorage.updateChunk(chunk);

    // complete the whole session if this was the last chunk
    if (chunk.isLastChunk) {
      // TODO: throw an error if there are any chunks on this session.id that are not yet synchronized
      await completeSessionUpload(
        secureAxios,
        session,
        chunk.durationAtEndMs,
        appInsights
      );
    }

    return true;
  } catch (err) {
    console.error("Chunk upload failed", err);
    appInsights.trackException({
      error: err as Error
    });

    return false;
  }
}

async function completeSessionUpload(
  secureAxios: Axios,
  session: LocalRecordingSession,
  durationMs: number,
  appInsights: ReactPlugin
) {
  try {
    // tell the server we're finished uploading chunks and it can do its thing
    await finishRecordingSession(
      secureAxios,
      session.id,
      session.startedDate,
      durationMs
    );

    // mark the session as synchronized and update that in indexedDB
    session.isSynchronized = true;
    await localRecordingStorage.updateRecordingSession(session);

    // delete the local recording session and its chunks
    const success =
      await localRecordingStorage.deleteOfflineRecordingSessionAndChunks(
        session.id
      );
    if (!success) {
      console.error(
        "Failed to delete local recording session and chunks after successful sync",
        session.id
      );
    }
  } catch (err) {
    appInsights.trackException({ error: err as Error });
    console.error("Failed finalizing the synced recording", err);
  }
}
